import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { invertKeyboardLayout } from '@/core/utils/keyboard';
import type { Item, Props } from '../types';
import { type API } from './useAPI';
import { isSelected, ITEM_ID_ALL } from '../utils';
import { useI18n } from '@/core/plugins/i18n';

/**
 * Функционал поиска
 */
export const useMenu = (
	model: Ref<Props['modelValue']>,
	items: Ref<Props['items']>,
	multiselect: Props['multiselect'],
	appendAllValue: Ref<Props['appendAllValue']>,
	appendSearchToResult: Ref<Props['appendSearchToResult']>,
	searchType: Ref<Props['searchType']>,
	minLength: number,
	api: API,
) => {
	/**
	 * Текст поиска по результатам
	 */
	const searchText = ref('');

	/**
	 * Объект, представляющий опцию "Все"
	 */
	const itemAll = {
		id: ITEM_ID_ALL,
		name: useI18n().Common.All,
	};

	/**
	 * Сброс поиска
	 */
	const resetSearch = () => {
		searchText.value = '';
		api.items.value = [];
	};

	/**
	 * Является ли строка поиска достаточной длины для отображения меню
	 */
	const genIsShort = () => {
		if (!api.apiRequest) return false;
		if (searchType.value !== 'inline') return false;
		if (searchText.value.length >= minLength) return false;

		return true;
	};

	/**
	 * Варианты выбора: props.items + "Выбрать все"
	 */
	const localItems: ComputedRef<Item[]> = computed(() => {
		const localItems: Item[] = [];

		if (!multiselect && appendAllValue.value) {
			localItems.push(itemAll);
		}

		items.value?.forEach(item => localItems.push({ ...item }));

		return localItems;
	});

	/**
	 * Подготовленный список меню, который содержит в сумме:
	 * - prop.items: указанный список с учетом фильтра и опций компонента
	 * - api.items: результаты api запроса, если используется api
	 *
	 * @returns Отфильтрованный массив элементов для отображения
	 */
	const itemsForShow = computed(() => {
		const searchString = searchText.value.toLowerCase();
		const searchStringInvertKeyboard = invertKeyboardLayout(searchString);

		let items: NonNullable<Props['items']> = [];

		localItems.value.forEach((item) => {
			const itemName = item.name.toLowerCase();

			if (
				item.id === Number(searchString) ||
				itemName.includes(searchString) ||
				itemName.includes(searchStringInvertKeyboard)
			) {
				if (itemName === searchString || itemName === searchStringInvertKeyboard) {
					items.unshift(item);
				} else {
					items.push(item);
				}
			}
		});

		items.push(...api.items.value);

		// ввод произвольного значения
		if (
			appendSearchToResult.value &&
			!!searchText.value &&
			(!items.length || items[0].name.toLowerCase() !== searchString)
		) {
			items.push({
				id: null,
				name: searchText.value,
			});
		}

		if (multiselect) {
			items = items.filter(item => !isSelected(model.value, item));
		}

		return items;
	});

	/**
	 * Выбрать элемент
	 *
	 * @param item Элемент для выбора
	 */
	const selectItem = (item: Item) => {
		if (multiselect && Array.isArray(model.value)) {
			if (!isSelected(model.value, item)) {
				model.value = [...model.value, item];
			}
		} else {
			model.value = item;
		}
	};

	/**
	 * Выбрать следующее значение
	 */
	const selectNextItem = () => {
		if (!Array.isArray(model.value)) {
			const currentIndex = itemsForShow.value.findIndex(item => item.id === (model.value as Item).id);
			const nextIndex = (currentIndex + 1) % itemsForShow.value.length;
			model.value = { ...itemsForShow.value[nextIndex] };
		}
	};

	/**
	 * Удалить выбранное значение по id
	 *
	 * @param id Идентификатор элемента для удаления
	 */
	const deleteItemById = async (id: Item['id']) => {
		if (Array.isArray(model.value)) {
			model.value = model.value.filter(item => item.id !== id);
		}
	};

	return {
		searchText,
		resetSearch,
		genIsShort,
		items: localItems,
		itemsForShow,
		selectItem,
		selectNextItem,
		deleteItemById,
	};
};
